import { HOME_TYPES } from "./homes.js";
import type { CatalogItem, ItemCategory, PetSpecies, Rarity, RoomId } from "./types.js";

const COLORS = ["moss", "clay", "cream", "bluebell", "walnut", "charcoal"];
const WOODS = ["oak", "walnut", "painted pine"];
/**
 * Upholstery colours, which are deliberately a shorter list than `COLORS`.
 *
 * The voxel art paints fabric in five colours, and a catalogue offering six was offering one the
 * renderer could not honour. The art is the expensive artefact, so the list bends to it.
 */
const FABRIC_COLORS = ["cream", "moss", "clay", "bluebell", "marigold"];

function item(input: {
  id: string;
  name: string;
  description: string;
  category: ItemCategory;
  priceCopper: number;
  socketTypes: string[];
  variants?: Record<string, string[]>;
  rarity?: Rarity;
  giftable?: boolean;
  stackable?: boolean;
  achievement?: string;
}): CatalogItem {
  return {
    id: input.id,
    name: input.name,
    description: input.description,
    category: input.category,
    priceCopper: input.priceCopper,
    socketTypes: input.socketTypes,
    variants: input.variants ?? { color: COLORS },
    rarity: input.rarity ?? "common",
    giftable: input.giftable ?? true,
    stackable: input.stackable ?? false,
    ...(input.achievement ? { achievement: input.achievement } : {})
  };
}

const furnitureSets = {
  hearth: ["Settle", "Side Table", "Bookshelf", "Rug", "Standing Lamp", "Tea Table", "Cushion", "Wall Shelf"],
  cottage: ["Bed", "Wardrobe", "Nightstand", "Dresser", "Quilt Rack", "Mirror", "Trunk", "Reading Chair"],
  kitchen: ["Cupboard", "Prep Table", "Stool", "Plate Rack", "Pantry", "Washstand", "Herb Shelf", "Dining Bench"],
  workshop: ["Writing Desk", "Tool Bench", "Drafting Stool", "Supply Shelf", "Display Cabinet", "Craft Table", "Map Rack", "Plant Stand"],
  gardenRoom: ["Wicker Chair", "Potting Table", "Seed Cabinet", "Fern Stand", "Small Fountain", "Bird Table", "Trellis Shelf", "Boot Rack"],
  gathering: ["Long Table", "Dining Chair", "Corner Bench", "Record Stand", "Game Shelf", "Serving Cart", "Floor Cushion", "Firewood Cradle"]
} as const;

export const FURNITURE_ITEMS: CatalogItem[] = Object.entries(furnitureSets).flatMap(([set, names], setIndex) =>
  names.map((name, index) =>
    item({
      id: `furniture-${set}-${index + 1}`,
      name,
      description: `A ${name.toLowerCase()} from the ${set.replace(/([A-Z])/g, " $1").toLowerCase()} collection.`,
      category: "furniture",
      priceCopper: 50 + ((setIndex * 8 + index) % 6) * 20,
      socketTypes: ["floor-small", "floor-medium", "wall"],
      // No orientation variant: which way a thing faces is a property of where it stands now,
      // not of the thing itself.
      variants: { wood: WOODS, fabric: FABRIC_COLORS }
    })
  )
);

const exteriorNames = [
  "Acorn Mailbox", "Bentwood Bench", "Bird Bath", "Boot Scraper", "Clothes Line", "Copper Lantern",
  "Creek Stones", "Garden Arch", "Hanging Bell", "Herb Cart", "Little Footbridge", "Log Pile",
  "Mossy Barrel", "Painted Birdhouse", "Picnic Blanket", "Porch Chair", "Rain Chain", "Round Table",
  "Scarecrow", "Stone Basin", "Story Post", "Sun Dial", "Tea Cart", "Tool Rack", "Village Banner",
  "Watering Can", "Weather Vane", "Wheelbarrow", "Wildflower Crate", "Wooden Swing"
];

export const EXTERIOR_ITEMS: CatalogItem[] = exteriorNames.map((name, index) =>
  item({
    id: `exterior-${index + 1}`,
    name,
    description: `A small outdoor detail for making the path to your home feel like yours.`,
    category: "exterior",
    priceCopper: 20 + (index % 5) * 10,
    socketTypes: index % 4 === 0 ? ["porch", "exterior-small"] : ["exterior-small", "exterior-medium"],
    // Finish only. The outdoor art is painted per finish and has no separate colour axis, and a
    // variant nothing can render is just a way to save a value that fails validation later.
    variants: { finish: ["new", "weathered"] }
  })
);

const plantNames = [
  "Basil", "Bluebell", "Chamomile", "Cherry Tomato", "Chives", "Daffodil", "Daisy", "Fern",
  "Foxglove", "Garden Pea", "Hydrangea", "Iris", "Lavender", "Lemon Balm", "Marigold", "Mint",
  "Nasturtium", "Pansy", "Parsley", "Rosemary", "Snapdragon", "Strawberry", "Sunflower", "Thyme"
];

export const PLANT_ITEMS: CatalogItem[] = plantNames.map((name, index) =>
  item({
    id: `plant-${index + 1}`,
    name,
    description: `${name} grows through four cheerful stages and never wilts.`,
    category: "plant",
    priceCopper: 20 + (index % 4) * 10,
    socketTypes: ["plant-bed"],
    variants: { pot: ["terracotta", "stone", "painted"], bloom: COLORS },
    stackable: true
  })
);

const petSpecies: PetSpecies[] = ["cat", "dog", "rabbit", "chicken", "turtle", "sheep"];
const petLooks: Record<PetSpecies, string[]> = {
  cat: ["tabby", "tuxedo", "ginger", "cream"],
  dog: ["brown", "black", "cream", "spotted"],
  rabbit: ["gray", "white", "brown", "black"],
  chicken: ["red", "white", "speckled", "black"],
  turtle: ["moss", "olive", "amber", "speckled"],
  sheep: ["white", "suffolk", "caramel", "pink"]
};

export const PET_ITEMS: CatalogItem[] = petSpecies.map((species) =>
  item({
    id: `pet-${species}`,
    name: species[0]!.toUpperCase() + species.slice(1),
    description: `A friendly ${species} that learns new animations as your bond grows.`,
    category: "pet",
    priceCopper: 500,
    socketTypes: ["pet"],
    variants: { appearance: petLooks[species] },
    giftable: false
  })
);

/**
 * One entry per home the world can actually build. Unlocking is what these record — all three
 * arrive together with the first quest, so they carry no price. Moving between unlocked homes
 * costs `HOME_CHANGE_FEE_COPPER` instead, which is charged on the choice rather than the item.
 */
export const HOUSE_ITEMS: CatalogItem[] = HOME_TYPES.map((home) =>
  item({
    id: home.itemId,
    name: home.name,
    description: home.description,
    category: "house",
    priceCopper: 0,
    socketTypes: ["house"],
    variants: {},
    giftable: false
  })
);

export const ROOM_ITEMS: CatalogItem[] = ["Living Room", "Kitchen", "Bedroom", "Hobby Room"].map((name, index) =>
  item({
    id: `room-${name.toLowerCase().replace(" ", "-")}`,
    name,
    description: `A focused cutaway ${name.toLowerCase()} with designed furniture sockets.`,
    category: "room",
    priceCopper: [0, 600, 800, 1_000][index]!,
    socketTypes: ["room"],
    variants: { floor: WOODS, wall: COLORS },
    giftable: false
  })
);

/**
 * Which room each catalogue entry opens.
 *
 * Written out rather than derived, because the two namings only look alike: `room-hobby-room` opens
 * `hobby`, and the "Living Room" tile is `living`. A member buys the catalogue item; what the
 * property records is the RoomId, and `catalog.test.ts` keeps the two halves honest.
 */
const ROOM_ITEM_IDS: Record<RoomId, string> = {
  living: "room-living-room",
  kitchen: "room-kitchen",
  bedroom: "room-bedroom",
  hobby: "room-hobby-room"
};

/** The catalogue entry that opens a room. */
export function roomItemId(room: RoomId): string {
  return ROOM_ITEM_IDS[room];
}

/** The room a catalogue entry opens, or null if the item is not a room. */
export function roomIdForItem(itemId: string): RoomId | null {
  const found = Object.entries(ROOM_ITEM_IDS).find(([, id]) => id === itemId);
  return found ? (found[0] as RoomId) : null;
}

/**
 * The commemorative tier, one item per milestone.
 *
 * The achievement strings used to cycle through four names across all twelve items, which meant
 * eight of them shared a milestone with something else and no item had a milestone of its own to
 * be earned by. One name each, and the split that was already here is what decides how it is
 * earned: the first four are `rare` and awarded by the Worker when the milestone is reached, the
 * rest are `event` and granted by a moderator when the occasion actually happens.
 */
const rareItems = [
  { name: "Founder's Lantern", achievement: "Founding neighbor" },
  { name: "Town Square Fountain", achievement: "Town Square contributor" },
  { name: "First Harvest Basket", achievement: "First full garden" },
  { name: "Moonlit Weather Vane", achievement: "Night visitor" },
  { name: "Golden Acorn Mailbox", achievement: "Golden post" },
  { name: "Community Quilt", achievement: "Quilt square" },
  { name: "Ghost Hunter Bell", achievement: "Ghost hunt night" },
  { name: "Storyteller's Chair", achievement: "Storyteller" },
  { name: "Winter Hearth", achievement: "Winter gathering" },
  { name: "Spring Festival Arch", achievement: "Spring festival" },
  { name: "Summer Firefly Jar", achievement: "Summer evening" },
  { name: "Autumn Leaf Cart", achievement: "Autumn harvest" }
];

export const RARE_ITEMS: CatalogItem[] = rareItems.map((entry, index) =>
  item({
    id: `rare-${index + 1}`,
    name: entry.name,
    description: `A commemorative item earned through a named Tribe milestone.`,
    category: "rare",
    priceCopper: 1_000 + (index % 5) * 1_000,
    socketTypes: ["exterior-medium", "floor-medium"],
    variants: { finish: ["original"] },
    rarity: index < 4 ? "rare" : "event",
    giftable: false,
    achievement: entry.achievement
  })
);

/**
 * The four milestones the Worker can see for itself, paired with what they award.
 *
 * Everything else in the rare tier commemorates something that happens off the server — a
 * festival, a quilt, a night of ghost hunting — and is granted by a moderator instead.
 */
export const EARNED_ACHIEVEMENTS = [
  { achievement: "Founding neighbor", itemId: "rare-1" },
  { achievement: "Town Square contributor", itemId: "rare-2" },
  { achievement: "First full garden", itemId: "rare-3" },
  { achievement: "Night visitor", itemId: "rare-4" }
] as const;

/** The catalogue entry a milestone awards, or null if that milestone is not one we grant. */
export function itemForAchievement(achievement: string): string | null {
  return RARE_ITEMS.find((entry) => entry.achievement === achievement)?.id ?? null;
}

export const CATALOG: CatalogItem[] = [
  ...HOUSE_ITEMS,
  ...ROOM_ITEMS,
  ...FURNITURE_ITEMS,
  ...EXTERIOR_ITEMS,
  ...PLANT_ITEMS,
  ...PET_ITEMS,
  ...RARE_ITEMS
];

export const CATALOG_COUNTS = CATALOG.reduce<Record<string, number>>((counts, catalogItem) => {
  counts[catalogItem.category] = (counts[catalogItem.category] ?? 0) + 1;
  return counts;
}, {});

export function getCatalogItem(id: string): CatalogItem {
  const result = CATALOG.find((catalogItem) => catalogItem.id === id);
  if (!result) throw new Error(`Unknown catalog item ${id}`);
  return result;
}
